/**
 * «Время записи»: загрузка салона по часам и рекомендация клиенту.
 * Замена окна «ВРЕМЯ» legacy. Один экран: в шапке дата — календарь на любой день, где у салона
 * есть слоты (≈ месяц); под ней плитки ближайших 14 дней — клик выделяет день квадратом; часы
 * выбранного дня; внизу — «вставить ссылку + рекомендованное время».
 * Чекбоксов у дней нет и при открытии ничего не отмечено (владелец 05.10.26): день попадает в текст,
 * когда в нём отметили час — сам или кнопкой «+ свободные часы» (`suggest`); на плитке такого дня
 * точка. «подобрать» — автоподбор сервера на ближайшие дни. Открывается на дне записи сделки,
 * если его передали (`day` — только у сделки в «Ждем клиента»).
 * Дальше 14 дней CRM загрузку не считает: часы есть, полосы нет — отметить можно.
 * Уровень часа несут длина полосы, цвет и слово.
 *   compact (панель расширения 320×420) — текст клиенту открывается глазом вместо списка часов;
 *   wide («Чаты») — часы и текст стоят вместе.
 * Клиенту компонент ничего не шлёт: `onInsert` кладёт текст в поле сообщения.
 * Спека `docs/specs/2026-10-05-zapis-time-design.md`.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarDays, Check, ChevronLeft, Eye, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RangeCalendar } from "@/components/RangeCalendar";
import { type Picked, type SlotDay, type SlotHour, loadSlots, loadSlotsText } from "@/lib/zapisSlots";

const ДНИ = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const МЕСЯЦЫ = "января февраля марта апреля мая июня июля августа сентября октября ноября декабря".split(" ");
const ОБНОВЛЯТЬ = 60_000;

const дата = (iso: string) => new Date(`${iso}T00:00`);
const подряд = (hours: number[]) =>
  hours.reduce<number[][]>((out, h) => {
    const last = out[out.length - 1];
    if (last && last[last.length - 1] === h - 1) last.push(h);
    else out.push([h]);
    return out;
  }, []);
/** Часы дня коротко для чипа: `15–17 · 20`. */
const коротко = (hours: number[]) =>
  подряд(hours)
    .map((c) => (c.length === 1 ? `${c[0]}` : `${c[0]}–${c[c.length - 1]}`))
    .join(" · ");
const дней = (n: number) => (n === 1 ? "день" : n < 5 ? "дня" : "дней");

/** Час нельзя отметить: прошёл или мест нет. Рыжий, пик и час без расчёта (день дальше 14 дней) —
 *  можно, руками. */
const закрыт = (h: SlotHour) => h.past || h.level === "red";
/** Штриховка пика: цвет один не скажет «не записываем» при зелёном проценте. */
const ШТРИХ = {
  backgroundImage: "repeating-linear-gradient(135deg, var(--warn) 0 3px, color-mix(in oklch, var(--warn) 35%, transparent) 3px 6px)",
};

const iconBtn =
  "grid size-9 flex-none place-items-center rounded-[var(--radius-ui)] border border-border bg-bg text-fg hover:border-border-strong hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";
const quietLink = "h-6 flex-none whitespace-nowrap px-0.5 text-[11.5px] text-accent-text hover:underline";
const primary =
  "h-9 min-w-0 flex-1 truncate rounded-[var(--radius-ui)] bg-accent px-3 text-[13px] font-medium text-white hover:bg-accent-text disabled:opacity-50 disabled:hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent";

export function SlotTime({
  locationId,
  day,
  wide = false,
  actionLabel,
  onInsert,
  onBack,
}: {
  /** Салон сделки (id CRM); нет — первый салон по порядку CRM. Смена салона здесь сделку не меняет. */
  locationId: number | null;
  /** День записи сделки `ГГГГ-ММ-ДД` (сделка в «Ждем клиента»): открыть на нём, если у салона на
   *  него есть слоты. */
  day: string | null;
  wide?: boolean;
  /** «Вставить в поле» / «В поле ответа». */
  actionLabel?: string;
  /** Положить текст (ссылка + рекомендация по отметкам) в поле сообщения; ответ — что получилось.
   *  Нет — только посмотреть загрузку (AI Lab: шаг free_slots робота), без текста клиенту. */
  onInsert?: (location: number, picked: Picked) => Promise<string>;
  onBack?: () => void;
}) {
  const [loc, setLoc] = useState<number | null>(locationId);
  const [sel, setSel] = useState<string | null>(null);
  // Отметки сотрудника; при открытии пусто. `null` — автоподбор сервера («подобрать»).
  const [own, setOwn] = useState<Picked | null>({});
  const [preview, setPreview] = useState(false);
  const q = useQuery({
    queryKey: ["zapis-slots", loc],
    queryFn: () => loadSlots(loc),
    refetchInterval: ОБНОВЛЯТЬ,
    placeholderData: (prev) => prev,
  });
  // Отметки на дни, которых в окне уже нет (панель открыта с вечера), сервер не примет.
  const known = new Set([...(q.data?.days ?? []), ...(q.data?.far ?? [])].map((d) => d.date));
  const picked: Picked = Object.fromEntries(
    Object.entries(own ?? q.data?.picked ?? {}).filter(([d, hs]) => known.has(d) && hs.length > 0),
  );
  const pickedDays = Object.keys(picked).sort();
  const location = q.data?.location.id ?? 0;
  const text = useQuery({
    queryKey: ["zapis-slots-text", location, JSON.stringify(picked)],
    queryFn: () => loadSlotsText(location, picked),
    enabled: own !== null && (preview || wide) && !!location,
    placeholderData: (prev) => prev,
  });
  const insert = useMutation({ mutationFn: () => onInsert!(location, picked) });

  // `relative`: календарь шапки встаёт от левого края строки — в панели 260 px ему нужна вся ширина.
  const top = (title: React.ReactNode, right: React.ReactNode, back?: () => void) => (
    <div className="relative flex h-6 flex-none items-center gap-1.5">
      {back && (
        <button type="button" onClick={back} title="Назад" className="-ml-1 grid size-6 place-items-center rounded-[var(--radius-ui)] text-fg-muted hover:bg-bg-hover hover:text-fg">
          <ChevronLeft size={17} aria-hidden />
        </button>
      )}
      {typeof title === "string" ? <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{title}</span> : title}
      {right}
    </div>
  );
  // Тесный вид: в настоящей панели 320×420 списку часов остаётся ≈ 180 px — отступы по 4 px,
  // чтобы 11 часов дня (Москва) стояли без прокрутки (замер на seva-hp 05.10.26, v0.25.0).
  const frame = `flex min-h-0 flex-col ${wide ? "gap-2" : "h-full gap-1"} text-[12px] text-fg`;
  if (q.isPending)
    return (
      <div className={frame} data-annot="время записи">
        {top("Время записи", null, onBack)}
        <p className="text-[12.5px] text-fg-muted">Считаю загрузку салона по часам…</p>
      </div>
    );
  if (q.isError)
    return (
      <div className={frame} data-annot="время записи">
        {top("Время записи", null, onBack)}
        <p role="alert" className="text-[12.5px] text-err">Загрузку салона получить не удалось: {q.error.message}</p>
      </div>
    );

  const r = q.data;
  const today = r.now.slice(0, 10);
  const all = [...r.days, ...(r.far ?? [])];
  const current = all.find((d) => d.date === sel) ?? all.find((d) => d.date === day) ?? r.days[0]!;
  const far = !r.days.includes(current);
  const toggle = (d: string, hour: number) => {
    const had = picked[d] ?? [];
    const next = had.includes(hour) ? had.filter((h) => h !== hour) : [...had, hour].sort((a, b) => a - b);
    setOwn({ ...picked, [d]: next });
    insert.reset();
  };
  // «+ свободные часы»: день в текст — с рекомендованными часами (их видно сразу и можно поправить).
  const suggestDay = (d: string) => {
    setOwn({ ...picked, [d]: r.suggest[d] ?? [] });
    insert.reset();
  };
  const reset = pickedDays.length ? (
    <button type="button" onClick={() => (setOwn({}), insert.reset())} title="Снять отметки со всех дней — выбрать дни самому" className={quietLink}>
      снять все
    </button>
  ) : (
    <button type="button" onClick={() => (setOwn(null), insert.reset())} title="Отметить ближайшие дни с самыми свободными часами — в текст клиенту" className={quietLink}>
      подобрать
    </button>
  );
  const salon = (
    <select
      value={r.location.id}
      onChange={(e) => (setLoc(Number(e.target.value)), setOwn({}), setSel(null), insert.reset())}
      title={`Салон: ${r.location.name} — сменить (сделку это не меняет)`}
      className="h-6 flex-none rounded-[var(--radius-ui)] border border-border bg-bg px-1 text-[12.5px] font-semibold text-fg-muted hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
    >
      {r.locations.map((l) => (
        <option key={l.id} value={l.id} title={l.name}>{l.short}</option>
      ))}
    </select>
  );

  // Плитка дня — одна кнопка: клик выделяет день квадратом. Точка — день уже в тексте клиенту.
  const days = (
    <div className={`grid flex-none grid-cols-7 ${wide ? "gap-1" : "gap-[3px]"}`} aria-label="Ближайшие 14 дней">
      {r.days.map((d) => {
        const t = дата(d.date);
        const on = d.date === current.date;
        const inText = !!picked[d.date];
        const name = `${t.getDate()} ${МЕСЯЦЫ[t.getMonth()]}`;
        return (
          <button
            key={d.date}
            type="button"
            aria-pressed={on}
            onClick={() => setSel(d.date)}
            title={`${name}${inText ? " · в тексте клиенту" : ""}${d.estimate ? " · загрузка — оценка по среднему графику" : ""}${d.hours.length ? "" : " · слотов нет"}`}
            className={`relative flex flex-col items-center justify-center gap-px rounded-[5px] border text-[10px] leading-none text-fg-muted focus-visible:outline-2 focus-visible:outline-accent ${wide ? "h-9" : "h-7"} ${
              on ? "border-accent bg-accent-dim" : `hover:bg-bg-hover ${d.estimate ? "border-dashed border-border" : "border-transparent"}`
            }`}
          >
            {ДНИ[t.getDay()]}
            <b className={`text-[11.5px] font-semibold ${d.date === today ? "text-accent-text" : d.estimate ? "text-fg-muted" : "text-fg"}`}>
              {t.getDate()}
              {d.estimate && <sup className="ml-px text-[7px] font-normal text-fg-faint">≈</sup>}
            </b>
            {inText && <span className="absolute right-[2px] top-[2px] size-[5px] rounded-full bg-accent" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
  const title = (
    <DayPick value={current.date} min={today} max={all[all.length - 1]!.date} onPick={setSel} />
  );

  const hours = (
    <Hours
      day={current}
      today={today}
      now={r.now}
      picked={picked[current.date] ?? []}
      wide={wide}
      hint={
        r.suggest[current.date]?.length ? (
          <button type="button" onClick={() => suggestDay(current.date)} title="Отметить самые свободные часы этого дня — в текст клиенту" className="text-accent-text hover:underline">
            + свободные часы
          </button>
        ) : far ? (
          "загрузки ещё нет"
        ) : (
          "свободных часов нет"
        )
      }
      onToggle={toggle}
    />
  );

  const chips = pickedDays.length > 0 && (
    <div className="flex flex-none flex-wrap gap-1" aria-label="дни в тексте">
      {pickedDays.map((d) => {
        const t = дата(d);
        return (
          <span key={d} className="inline-flex h-[22px] items-center gap-0.5 whitespace-nowrap rounded-full border border-accent bg-accent-dim pl-2 pr-0.5 text-[11.5px] font-medium leading-none text-accent-text">
            {d === today ? "сегодня" : `${ДНИ[t.getDay()]} ${t.getDate()}`} · {коротко(picked[d]!)}
            <button
              type="button"
              title="Убрать день из текста"
              onClick={() => (setOwn({ ...picked, [d]: [] }), insert.reset())}
              className="grid size-4 place-items-center rounded-full hover:bg-bg"
            >
              <X size={11} strokeWidth={2.5} aria-hidden />
            </button>
          </span>
        );
      })}
    </div>
  );
  const recommendation = own === null ? r.text : (text.data?.text ?? "");
  const message = (
    <div className={`overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 text-[12px] leading-[1.35] ${wide ? "" : "min-h-0 flex-1"}`} aria-label="Текст клиенту">
      {r.invite}
      <span className="text-fg-faint">{"\n"}‹ссылка клиента — подставится при вставке›</span>
      {pickedDays.length > 0 ? `\n\n${recommendation || "…"}` : <span className="text-fg-muted">{"\n\n"}Дни не отмечены — в тексте будет только ссылка.</span>}
      {text.isError && <span className="text-err">{`\n${text.error.message}`}</span>}
    </div>
  );

  const label = pickedDays.length ? `${actionLabel}: ссылка + ${pickedDays.length} ${дней(pickedDays.length)}` : `${actionLabel}: только ссылка`;
  const foot = (
    <div className={`flex flex-none flex-col gap-1 ${wide ? "" : "mt-auto border-t border-border pt-1.5"}`}>
      <div className="flex gap-1.5">
        <button type="button" className={primary} disabled={insert.isPending} onClick={() => insert.mutate()} title="Текст встанет в поле сообщения — отправляете вы">
          {insert.isPending ? "Готовлю текст…" : label}
        </button>
        {!wide && (
          <button type="button" className={iconBtn} aria-pressed={preview} onClick={() => setPreview((v) => !v)} title={preview ? "Назад к часам" : "Посмотреть текст клиенту"}>
            <Eye size={15} aria-hidden />
          </button>
        )}
      </div>
      {insert.isSuccess && <p role="status" className="rounded-[var(--radius-ui)] bg-ok-bg px-2 py-1 text-[12px] leading-snug text-ok">{insert.data}</p>}
      {insert.isError && <p role="alert" className="rounded-[var(--radius-ui)] bg-err-bg px-2 py-1 text-[12px] leading-snug text-err">{insert.error.message}</p>}
    </div>
  );

  if (!wide && preview)
    return (
      <div className={frame} data-annot="время записи: текст">
        {top("Текст клиенту", reset, () => setPreview(false))}
        {chips}
        {message}
        {foot}
      </div>
    );
  return (
    <div className={frame} data-annot="время записи">
      {top(title, <>{onInsert && reset}{salon}</>, onBack)}
      {days}
      {hours}
      {onInsert && wide && chips}
      {onInsert && wide && message}
      {onInsert && foot}
    </div>
  );
}

/** Дата в шапке окна: кнопка с выбранным днём и календарь — день дальше плиток (до `max`, пока у
 * салона есть слоты). Плитки — для ближних дней, календарь — для дальних. */
function DayPick({ value, min, max, onPick }: { value: string; min: string; max: string; onPick: (day: string) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  const t = дата(value);
  return (
    // Обёртка без `relative`: календарь отсчитывает место от строки шапки, а не от кнопки.
    <div ref={box} className="flex min-w-0 flex-1" data-annot="дата записи">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Выбрать другую дату — календарь"
        className={`inline-flex h-6 min-w-0 items-center gap-1 rounded-[var(--radius-ui)] border bg-bg px-1.5 text-[12.5px] font-semibold text-fg focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent ${
          open ? "border-border-strong" : "border-border hover:border-border-strong"
        }`}
      >
        <CalendarDays size={13} className="flex-none text-fg-muted" aria-hidden />
        <span className="truncate">
          {ДНИ[t.getDay()]} {t.getDate()} {МЕСЯЦЫ[t.getMonth()]}
        </span>
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Дата записи"
          className="absolute left-0 top-full z-30 mt-0.5 w-[264px] max-w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg"
        >
          <RangeCalendar
            from={value}
            to={value}
            single
            min={min}
            max={max}
            onPick={(day) => {
              setOpen(false);
              onPick(day);
            }}
          />
        </div>
      )}
    </div>
  );
}

function Hours({
  day,
  today,
  now,
  picked,
  wide,
  hint,
  onToggle,
}: {
  day: SlotDay;
  today: string;
  now: string;
  picked: number[];
  wide: boolean;
  /** Что показать, когда у дня нет отмеченных часов: кнопка «+ свободные часы» или причина. */
  hint: React.ReactNode;
  onToggle: (day: string, hour: number) => void;
}) {
  const t = дата(day.date);
  const nowHour = day.date === today ? Number(now.slice(11, 13)) : -1;
  return (
    <div className={`flex min-h-0 flex-col ${wide ? "" : "flex-1"}`}>
      <div className="mb-[3px] flex h-[15px] flex-none items-center gap-1.5 whitespace-nowrap text-[11.5px] leading-[15px] text-fg-muted">
        <b className="min-w-0 truncate text-[12px] font-semibold text-fg">
          {ДНИ[t.getDay()]} {t.getDate()} {МЕСЯЦЫ[t.getMonth()]}
          {day.date === today ? ` · сегодня ${now.slice(11)}` : ""}
        </b>
        {day.estimate && <span className="text-fg-faint" title="Дальше текущей недели: загрузка по среднему графику, не факт">≈ оценка</span>}
        {picked.length > 0 ? (
          <span className="ml-auto inline-flex flex-none items-center gap-0.5 font-medium text-accent-text">
            <Check size={10} strokeWidth={3} aria-hidden /> {picked.length} ч в тексте
          </span>
        ) : (
          <span className="ml-auto flex-none text-fg-faint">{hint}</span>
        )}
      </div>
      <div className={`flex min-h-0 flex-col ${wide ? "" : "flex-1 overflow-y-auto"}`}>
        {day.hours.length === 0 && <p className="py-2 text-[12px] text-fg-muted">На этот день слотов записи нет.</p>}
        {day.hours.map((h) => (
          <Hour key={h.hour} h={h} on={picked.includes(h.hour)} wide={wide} current={h.hour === nowHour} onToggle={() => onToggle(day.date, h.hour)} />
        ))}
      </div>
    </div>
  );
}

function Hour({ h, on, wide, current, onToggle }: { h: SlotHour; on: boolean; wide: boolean; current: boolean; onToggle: () => void }) {
  const off = закрыт(h);
  const pct = `${h.estimate ? "≈" : ""}${h.load ?? 0}%`;
  const inner =
    h.level === "red" ? `мест нет · ${pct}` : h.level === "orange" ? `плотно · ${pct}` : h.level === "ok" && (h.load ?? 0) >= 28 ? pct : "";
  const status =
    h.past
      ? current ? "идёт" : "прошёл"
      : h.level === "none"
        ? "без расчёта"
        : h.level === "red"
          ? h.max ? `нет · макс ${h.max}` : "мест нет"
          : `${h.free ?? 0} св.${wide ? ` · ${h.cur ?? 0} зап.` : ""}`;
  const tone = h.past || h.level === "none" ? "text-fg-faint" : h.level === "red" ? "font-semibold text-err" : h.level === "ok" ? "font-semibold text-fg" : "font-semibold text-warn";
  const fill = h.past ? "bg-border-strong" : h.level === "red" ? "bg-err" : h.level === "orange" ? "bg-warn" : h.level === "peak" ? "" : "bg-ok";
  return (
    <>
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        disabled={off}
        onClick={onToggle}
        title={
          h.level === "none"
            ? "Загрузка на этот час ещё не посчитана — отметить можно"
            : `записано ${h.cur ?? 0} · свободно с запасом ${h.free ?? 0} · максимум ${h.max ?? 0} · стилистов ${h.stylists ?? 0} · загрузка ${pct}${h.level === "peak" ? " · пик выходных: по регламенту не записываем" : ""}`
        }
        className={`-mx-[3px] grid flex-none items-center gap-1.5 rounded-[4px] px-[3px] text-left ${wide ? "h-5 grid-cols-[16px_40px_minmax(0,1fr)_92px] text-[12.5px]" : "h-4 grid-cols-[16px_36px_minmax(0,1fr)_74px] text-[12px]"} ${
          on ? "bg-accent-dim" : off ? "" : "hover:bg-bg-hover"
        } ${off ? "cursor-default" : "cursor-pointer"} focus-visible:outline-2 focus-visible:outline-accent`}
      >
        <span className={`grid size-[13px] place-items-center rounded-[3px] border-[1.5px] ${off ? "invisible" : on ? "border-accent bg-accent" : h.level === "peak" || h.level === "orange" ? "border-warn bg-bg" : "border-border-strong bg-bg"}`} aria-hidden>
          {on && <Check size={10} strokeWidth={3.2} className="text-bg" />}
        </span>
        <span className={`font-mono text-[11.5px] tabular-nums ${h.past ? "text-fg-faint line-through" : "text-fg"}`}>
          {String(h.hour).padStart(2, "0")}:00
        </span>
        <span className={`relative overflow-hidden rounded-[2px] bg-bg-hover ${wide ? "h-[11px]" : "h-2.5"}`}>
          {h.level !== "none" && (
            <i
              className={`absolute inset-y-0 left-0 flex items-center overflow-hidden whitespace-nowrap rounded-[2px] text-[9.5px] font-semibold not-italic leading-none ${fill} ${h.past ? "pl-1 text-fg-muted" : h.level === "orange" || h.level === "peak" ? "text-black/80" : "pl-1 text-bg"} ${h.level === "orange" ? "pl-1" : ""} ${h.estimate ? "opacity-60" : ""}`}
              style={{ width: `${Math.min(h.load ?? 0, 100)}%`, minWidth: h.level === "peak" && !h.past ? 26 : undefined, ...(h.level === "peak" && !h.past ? ШТРИХ : {}) }}
            >
              {h.level === "peak" && !h.past ? <b className="flex h-full items-center bg-warn px-1 font-semibold">пик{(h.load ?? 0) >= 40 ? ` · ${pct}` : ""}</b> : inner}
            </i>
          )}
        </span>
        <span className={`truncate text-right tabular-nums ${tone}`}>{status}</span>
      </button>
      {current && <div role="separator" aria-label="сейчас" className="relative -mx-[3px] my-px h-px flex-none bg-accent before:absolute before:left-0 before:top-[-2px] before:size-[5px] before:rounded-full before:bg-accent" />}
    </>
  );
}
